import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownItem,
  DropdownMenu,
} from "reactstrap";
import React, { useContext, useMemo, useState, Fragment } from "react";
import PropTypes from "prop-types";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import withRedirect from "../../helpers/hoc/withRedirect";
import ButtonIcon from "../common/ButtonIcon";
import {
  faEye,
  faFileExcel,
  faFilter,
  faMoneyCheck,
  faPlus,
  faSearch,
  faTag,
  faToggleOn,
} from "@fortawesome/free-solid-svg-icons";
import { useParams } from "react-router";
import NumberFormat from "react-number-format";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import {
  activateOrDeactivateFinances,
  deletefinance,
  fetchCommonCost,
  fetchCreateCommonCost,
  fetchCreateIndividualCost,
  toggleIndividualStatus,
} from "../../helpers/api/finance";
import { findAllExchangeRates } from "../../helpers/api/currency";
import { isIterableArray } from "../../helpers/utils";
import FullPagination from "../common/pagination/FullPagination";
import { useSelector } from "react-redux";
import { Link, useHistory, useLocation } from "react-router-dom";
import { PreloadState } from "../UI/preloading/PreloadState";
import Datetime from "react-datetime";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SupportLink from "../root/support/components/SupportLink";
import moment from "moment";
import Swal from "sweetalert2";
import { CurrencyContext } from "../../context/Context";
import { Toast } from "../UI/toast/Toast";
import FalconDropzone from "../common/FalconDropzone";
import DownloadFormatDropdown from "../common/DownloadFormatDropdown";
import Flex from "../common/Flex";
import Filter from "../common/Filter";
import handleErrorMessage from "../../helpers/errors/handleErrorMessage";
import { SUCCESS_RESPONSE } from "../../helpers/codeResponses/codes";
import useModuleBase from "../../helpers/hooks/useModuleBase";
import { fetchAllPages } from "../../helpers/api/pagination";

const IndividualCosts = ({
  setRedirect,
  setRedirectUrl,
  onEditAction,
  options,
  alternativeName,
  showPagination = true,
  ...rest
}) => {
  const moduleBase = useModuleBase();

  const { selectedCondominium } = useSelector((state) => state.condominium);
  // El tipo viaja en el location.state desde las tarjetas de Finanzas /
  // Contabilidad. Sin él (miga de pan, URL escrita, recarga) usamos Gastos
  // particulares, que es el único tipo que atiende esta pantalla.
  const { state: locationState } = useLocation();
  const state = locationState?.type_id
    ? locationState
    : { type_id: 3, type_name: "Gastos particulares" };
  const history = useHistory();
  const [formDate, setformDate] = useState({ since: null, to: null });
  const { currency: curr, baseCurrency } = useContext(CurrencyContext);
  const [loading, setLoading] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState([]);
  const [ownersModalOpen, setOwnersModalOpen] = useState(false);
  const [ownersModalList, setOwnersModalList] = useState([]);
  const [ratesByDate, setRatesByDate] = useState({});
  const [latestRate, setLatestRate] = useState(null);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const { id } = useParams();

  const [params, setParams] = useState({
    state: 0,
    condominiumId: id,
    start_date: null,
    end_date: null,
    type_id: state?.type_id,
    active: 1,
  });

  // Memoizado igual que en CommonCosts.jsx: sin esto, useSWRFetchPagination
  // recibe un objeto nuevo en cada render y queda pidiendo los datos sin
  // parar (ver useSWRFetchPagination.js).
  const paramsWithRefresh = useMemo(
    () => ({ ...params, refreshIndex }),
    [params, refreshIndex]
  );

  const filter_options = [
    { value: 1, displayName: "Activos", disabled: false },
    { value: 0, displayName: "Inactivos", disabled: false },
    { value: "", displayName: "Todos", disabled: false },
  ];

  const {
    data,
    isLoading,
    meta,
    mutate,
    handler: { nextPage, previousPage, perPage, goToPage },
  } = useSWRFetchPagination(fetchCommonCost, paramsWithRefresh);

  React.useEffect(() => {
    let isMounted = true;

    const fetchRates = async () => {
      if (!curr?.currency?.id || Number(curr?.value ?? 1) === 1) {
        if (isMounted) {
          setRatesByDate({});
          setLatestRate(null);
        }
        return;
      }

      try {
        const response = await fetchAllPages((page, pageSize) => findAllExchangeRates(page, pageSize, {
          currency: curr.currency.id,
        }));

        const rates = response?.data || [];
        const normalizedByDate = rates.reduce((acc, rate) => {
        const dateSource = rate?.date || rate?.created_at;
        const key = moment(dateSource).format("YYYY-MM-DD");
        const value = Number(rate?.value || 0);

          if (!dateSource || value <= 0) {
            return acc;
          }

          const current = acc[key];
          if (!current || moment(dateSource).valueOf() > current.sortValue) {
            acc[key] = {
              value,
              sortValue: moment(dateSource).valueOf(),
            };
          }

          return acc;
        }, {});

        const latest = rates
          .filter((rate) => Number(rate?.value || 0) > 0)
          .sort(
            (a, b) =>
              moment(b.date || b.created_at).valueOf() -
              moment(a.date || a.created_at).valueOf()
          )[0];

        if (isMounted) {
          setRatesByDate(normalizedByDate);
          setLatestRate(Number(latest?.value || 0) || null);
        }
      } catch (error) {
        if (isMounted) {
          setRatesByDate({});
          setLatestRate(Number(curr?.value ?? 0) || null);
        }
      }
    };

    fetchRates();

    return () => {
      isMounted = false;
    };
  }, [curr]);

  const resolveDisplayRate = (record) => {
    const rawStoredRate = record?.exchange_rate?.value ?? record?.exchange_rate ?? null;
    const storedRate = Number(rawStoredRate || 0);

    if (storedRate > 0) {
      return storedRate;
    }

    const recordDate = record?.expired_at || record?.payment_date || record?.created_at;
    const dateKey = recordDate ? moment(recordDate).format("YYYY-MM-DD") : null;
    const datedRate = dateKey ? Number(ratesByDate?.[dateKey]?.value || 0) : 0;

    if (datedRate > 0) {
      return datedRate;
    }

    return Number(latestRate || curr?.value || 1);
  };

  const resolveOwnersSummary = (cost) => {
    const owners = (cost?.properties || [])
      .map((item) => {
        const firstname = item?.property?.user?.data?.firstname || "";
        const lastname = item?.property?.user?.data?.lastname || "";
        return `${firstname} ${lastname}`.trim();
      })
      .filter(Boolean);

    if (!owners.length) {
      return "Sin propietario asignado";
    }

    const units = cost?.properties?.length || owners.length;

    if (units === 1) {
      return owners[0];
    }

    return `${owners[0]} + ${units - 1} unidad(es) más`;
  };

  const resolveOwnersList = (cost) => {
    const owners = (cost?.properties || [])
      .map((item) => {
        const firstname = item?.property?.user?.data?.firstname || "";
        const lastname = item?.property?.user?.data?.lastname || "";
        const username = item?.property?.user?.username || "";
        const fullName = `${firstname} ${lastname}`.trim();

        if (!fullName && !username) {
          return "Propietario sin nombre";
        }

        return username ? `${fullName} (${username})`.trim() : fullName;
      })
      .filter(Boolean);

    return [...new Set(owners)];
  };

  const handleOpenOwnersModal = (cost) => {
    setOwnersModalList(resolveOwnersList(cost));
    setOwnersModalOpen(true);
  };

  const toggleImportModal = () => {
    setImportModalOpen(!importModalOpen);
    setImportFile([]);
  };

  const handleImport = async () => {
    if (importFile.length === 0) {
      Toast.fire({
        type: "error",
        title: "Por favor seleccione un archivo.",
      });
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("file", importFile[0].file);

      Toast.fire({
        type: "info",
        title: "Cargando archivo...",
      });

      const response = await fetchCreateIndividualCost(
        id || selectedCondominium,
        formData
      );

      Toast.fire({
        type: "success",
        title: "Gastos particulares importados exitosamente",
      });

      toggleImportModal();
      setParams((prevParams) => ({ ...prevParams }));
    } catch (error) {
      if (error?.data?.errors && Array.isArray(error.data.errors)) {
        const errorList = error.data.errors.join('\n');
        Toast.fire({
          type: "error",
          title: error.data.message || "Errores en la importación",
          html: `<pre style="text-align: left; font-size: 12px; max-height: 300px; overflow-y: auto;">${errorList}</pre>`,
          timer: 10000,
          timerProgressBar: true,
        });
      } else {
        Toast.fire({
          type: "error",
          title: error?.data?.error || error?.data?.message || "Error al importar gastos particulares",
          timer: 5000,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // HO-TASK-249: la plantilla existe en los tres formatos que acepta la carga.
  const downloadTemplate = (format = "xlsx") => {
    // Ruta relativa al propio frontend (la plantilla vive en public/, Vite la
    // sirve directo) — VITE_URL es la URL del backend, donde este archivo no existe.
    const link = document.createElement("a");
    link.href = `/expenses.${format}`;
    link.setAttribute("download", `expenses.${format}`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSeeSingleForm = () => {
    if (selectedCondominium) {
      history.push(`/dashboard/${moduleBase}/${selectedCondominium}/form`);
    }
  };

  const handleDeactivate = async (cost) => {
    try {
      const { code } = await toggleIndividualStatus(cost.id, 0);
      if (code === SUCCESS_RESPONSE.OK) {
        Toast.fire({
          type: "success",
          title: "Gasto particular desactivado exitosamente.",
        });
        setRefreshIndex((prev) => prev + 1);
        mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleActivate = async (cost) => {
    try {
      const { code } = await toggleIndividualStatus(cost.id, 1);
      if (code === SUCCESS_RESPONSE.OK) {
        Toast.fire({
          type: "success",
          title: "Gasto particular activado exitosamente.",
        });
        setRefreshIndex((prev) => prev + 1);
        mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const handleActivateAll = async () => {
    try {
      const { code } = await activateOrDeactivateFinances(selectedCondominium, {
        type_id: state.type_id,
        active: 1,
      });

      if (code === SUCCESS_RESPONSE.SUCCESFUL) {
        Toast.fire({
          title: "Gastos particulares activados correctamente.",
          type: "success",
        });
        setRefreshIndex((prev) => prev + 1);
        mutate();
      }
    } catch (error) {
      handleErrorMessage(error);
    }
  };

  const deleteCost = async (costId) => {
    Swal.fire({
      title: "¿Estas seguro?",
      text: "¿Quieres eliminar este gasto particular?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#9341af",
      cancelButtonColor: "#bbb",
      confirmButtonText: "Si, eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.value) {
        try {
          const res = await deletefinance(costId);
          if (res.code === SUCCESS_RESPONSE.OK) {
            Toast.fire({
              type: "success",
              title: `Se ha eliminado el gasto particular satisfactoriamente.`,
            });
            setRefreshIndex((prev) => prev + 1);
            mutate();
          }
        } catch (error) {
          handleErrorMessage(error);
        }
      }
    });
  };

  const ButtonAdd = ({ onClick }) => {
    return (
      <ButtonIcon
        className="ml-2"
        color="falcon-default"
        onClick={onClick}
        icon={faPlus}
      >
        Añadir {state.type_name}
      </ButtonIcon>
    );
  };

  return (
    <Fragment>
      <PageHeader
        title={state.type_name}
        titleTag="h2"
        description="Gestiona y administra cada una de tus finanzas."
        className="mb-3"
        col={{ lg: 9 }}
      >
        <SupportLink />
      </PageHeader>
      <Fragment>
        <Card>
          <FalconCardHeader title={state.type_name}>
            <Row className="align-items-center justify-content-between mb-3">
              <Col xs="auto" className="d-flex align-items-center mb-2 mb-md-0">
                <Label className="mr-2 mb-0 font-weight-semi-bold">Filtrar</Label>
                <Input
                  type="select"
                  className="form-control"
                  style={{ minWidth: "160px", width: "auto" }}
                  value={params.active !== undefined ? params.active : 1}
                  onChange={(e) => {
                    const val = e.target.value;
                    setParams((prev) => ({
                      ...prev,
                      active: val === "" ? "" : Number(val),
                    }));
                  }}
                >
                  {filter_options.map((val, i) => (
                    <option key={i} value={val.value} disabled={val.disabled}>
                      {val.displayName}
                    </option>
                  ))}
                </Input>
              </Col>
              <Col xs="auto">
                <Row className="align-items-center justify-content-center">
                  <Col
                    xs="auto"
                    className="d-flex align-items-center justify-content-center"
                  >
                    <Label className="mb-0 mr-2">
                      <FontAwesomeIcon icon={faFilter} />
                    </Label>
                  </Col>
                  <Col
                    xs="auto"
                    className="d-flex align-items-center flex-sm-column flex-md-column flex-lg-row"
                  >
                    <Label className="mr-2 mb-0">Desde</Label>
                    <Datetime
                      value={formDate.since}
                      isValidDate={(current, selected) => {
                        return !current.isAfter();
                      }}
                      onChange={(d) =>
                        setformDate((prev) => ({ ...prev, since: d }))
                      }
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                    />
                  </Col>
                  <Col
                    xs="auto"
                    className="d-flex align-items-center flex-sm-column flex-md-column flex-lg-row"
                  >
                    <Label className="mr-2 mb-0">Hasta</Label>
                    <Datetime
                      value={formDate.to}
                      className="mr-2"
                      isValidDate={(current, selected) => {
                        return (
                          current.isAfter(formDate.since) && !current.isAfter()
                        );
                      }}
                      dateFormat="DD/MM/YYYY"
                      timeFormat={false}
                      onChange={(d) =>
                        setformDate((prev) => ({ ...prev, to: d }))
                      }
                    />
                  </Col>
                  <Col
                    xs="auto"
                    className="pl-0 d-flex align-items-md-end align-items-lg-center align-items-sm-end"
                  >
                    <ButtonIcon
                      className="text-nowrap d-sm-none d-lg-block"
                      disabled={!formDate.to}
                      icon={faSearch}
                      color="falcon-default"
                      onClick={() => {
                        setParams((prev) => {
                          return {
                            ...prev,
                            start_date: formDate.since ? formDate.since.format("YYYY-MM-DD") : null,
                            end_date: formDate.to ? formDate.to.format("YYYY-MM-DD") : null,
                          };
                        });
                      }}
                    >
                      Buscar
                    </ButtonIcon>
                    <Button
                      className="text-nowrap d-sm-block d-lg-none"
                      disabled={!formDate.to}
                      color="falcon-default"
                      onClick={() => {
                        setParams((prev) => {
                          return {
                            ...prev,
                            start_date: formDate.since ? formDate.since.format("YYYY-MM-DD") : null,
                            end_date: formDate.to ? formDate.to.format("YYYY-MM-DD") : null,
                          };
                        });
                      }}
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
            <hr />
            <Row className="justify-content-end align-items-center">
              <Col
                xs={12}
                md={"auto"}
                className="text-center text-md-right my-1"
              >
                {state?.type_name === "Gastos particulares" && (
                  <>
                    <ButtonIcon
                      className="mb-2 mb-md-0"
                      color="falcon-success"
                      icon={faToggleOn}
                      onClick={handleActivateAll}
                    >
                      Activar gastos
                    </ButtonIcon>
                    {/*  <ButtonIcon
                      className="mb-2 mb-md-0"
                      icon={faDownload}
                      color="falcon-default"
                      onClick={downloadTemplate}
                    /> */}

                    <ButtonIcon
                      className="ml-2 mb-2 mb-md-0"
                      icon={faFileExcel}
                      color="falcon-default"
                      onClick={toggleImportModal}
                      disabled={loading}
                    >
                      {loading ? "Cargando..." : "Cargar archivo"}
                    </ButtonIcon>
                  </>
                )}

                <ButtonIcon
                  className="text-nowrap ml-2 mb-2 mb-md-0"
                  icon={faMoneyCheck}
                  color="falcon-default"
                  onClick={handleSeeSingleForm}
                >
                  Previsualizar planilla
                </ButtonIcon>

                <ButtonAdd
                  className="ml-2"
                  onClick={() =>
                    history.push({
                      pathname: `/dashboard/${moduleBase}/${selectedCondominium}/individual-costs/create`,
                      state: {
                        type_id: state.type_id,
                        type_name: state.type_name,
                      },
                    })
                  }
                />
              </Col>
            </Row>
          </FalconCardHeader>
          <CardBody className="fs--1 p-0">
            <PreloadState cond={isIterableArray(data)} loading={isLoading}>
              {data.length > 0 ? (
                <>
                  {data.map((cost, index) => (
                    <Fragment key={index}>
                      <Col className=" shadow-none border-top">
                        <Row className="mb-3 mt-3">
                          <Col className="d-flex align-items-center">
                            <div className="media-body">
                              <h6 className="mb-0 font-weight-semi-bold d-flex align-items-center">
                                <span className="text-900 mr-2">
                                  <p className="m-0">{cost.name}</p>
                                </span>
                                {cost.sub_expenses?.[0]?.accounting_area && (
                                  <span className="badge badge-soft-primary border-primary fs--2">
                                    <FontAwesomeIcon icon={faTag} className="mr-1" />
                                    {cost.sub_expenses[0].accounting_area.name}
                                  </span>
                                )}
                              </h6>
                              <h6 className="m-0 mt-2">
                                {resolveOwnersSummary(cost)}
                              </h6>
                              {Number(cost?.properties?.length || 0) > 1 && (
                                <div className="d-flex align-items-center mt-1">
                                  <small className="text-600 mr-2">
                                    Aplicado a {cost.properties.length} unidades
                                  </small>
                                  <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 text-primary"
                                    onClick={() => handleOpenOwnersModal(cost)}
                                    title="Ver propietarios"
                                  >
                                    <FontAwesomeIcon icon={faEye} />
                                  </Button>
                                </div>
                              )}
                              <p className="m-0">{cost.description}</p>
                              <p className="m-0">
                                creada el{" "}
                                {moment(cost.created_at).format("DD-MM-YYYY")}
                              </p>
                            </div>
                          </Col>
                          <Col>
                            <Row>
                              <Col>
                                <p className="text-right">Monto</p>
                              </Col>
                            </Row>
                            <Row>
                              <Col>
                                <h6 className="text-right">
                                  <NumberFormat
                                    prefix={`${baseCurrency.symbol} `}
                                    allowedDecimalSeparators={true}
                                    decimalScale={2}
                                    fixedDecimalScale={true}
                                    thousandSeparator={true}
                                    displayType="text"
                                    value={cost.value}
                                  />
                                  <NumberFormat
                                    className="text-right"
                                    displayType="text"
                                    allowedDecimalSeparators={true}
                                    thousandSeparator={true}
                                    decimalScale={2}
                                    fixedDecimalScale
                                    prefix={" | " + curr?.currency?.iso + " "}
                                    value={cost.value * resolveDisplayRate(cost)}
                                  />
                                </h6>
                              </Col>
                            </Row>
                          </Col>
                          <Col
                            xs="auto"
                            className="d-sm-flex align-items-center"
                          >
                            <UncontrolledDropdown
                              direction="left"
                              className="d-inline-block ml-2"
                            >
                              <DropdownToggle color="falcon-default" size="sm">
                                <FontAwesomeIcon icon="ellipsis-h" />
                              </DropdownToggle>
                              <DropdownMenu>
                                {cost.is_active ? (
                                  <DropdownItem
                                    className="text-danger"
                                    onClick={() => handleDeactivate(cost)}
                                  >
                                    Desactivar
                                  </DropdownItem>
                                ) : (
                                  <DropdownItem
                                    className="text-success"
                                    onClick={() => handleActivate(cost)}
                                  >
                                    Activar
                                  </DropdownItem>
                                )}
                                <DropdownItem
                                  onClick={() =>
                                    history.push({
                                      pathname: `/dashboard/${moduleBase}/${id}/individual-costs/edit/${cost.id}`,
                                      state: {
                                        type_id: state.type_id,
                                        type_name: state.type_name,
                                      },
                                    })
                                  }
                                >
                                  Editar
                                </DropdownItem>
                                <DropdownItem
                                  className="text-danger"
                                  onClick={() => deleteCost(cost.id)}
                                >
                                  Eliminar
                                </DropdownItem>
                              </DropdownMenu>
                            </UncontrolledDropdown>
                          </Col>
                        </Row>
                      </Col>
                    </Fragment>
                  ))}
                </>
              ) : (
                <Row className="p-card">
                  <Col>
                    <Alert color="info" className="mb-0">
                      Al parecer aún no hay cuentas para mostrar. Puedes crear
                      una ahora mismo pulsando el siguiente enlace
                      <Button
                        data-testid="Button"
                        tag={Link}
                        color="link"
                        size="sm"
                        className="ml-1 pl-0"
                        onClick={() =>
                          history.push({
                            pathname: `/dashboard/${moduleBase}/${selectedCondominium}/individual-costs/create`,
                            state: {
                              type_id: state.type_id,
                              type_name: state.type_name,
                            },
                          })
                        }
                      >
                        Crear gasto particular
                      </Button>
                    </Alert>
                  </Col>
                </Row>
              )}
              {/*               <Or value={false} cond={error ? "error" : "ok"}>
                <Or value={"error"}>
                  <Row className="p-card">
                    <Col>
                      <Alert color="danger" className="mb-0">
                        Esta página no está disponible
                        <Button
                          data-testid="Button"
                          color="link"
                          size="sm"
                          className="ml-1 pl-0"
                          onClick={() => {
                            setRedirectUrl(`/dashboard/finance`);
                            setRedirect(true);
                          }}
                        >
                          Volver
                        </Button>
                      </Alert>
                    </Col>
                  </Row>
                </Or>
                <Or value={"ok"}>
                  
                </Or>
              </Or> */}
              {showPagination && (
                /*      <CardFooter className="border-top"> */
                <FullPagination
                  nextPage={nextPage}
                  prevPage={previousPage}
                  goToPage={goToPage}
                  meta={meta}
                  className={"shadow-none"}
                  posText={state.type_name}
                  perPage={perPage}
                />
                /*       </CardFooter> */
              )}
            </PreloadState>
          </CardBody>
        </Card>
        <Modal isOpen={importModalOpen} toggle={toggleImportModal}>
          <ModalHeader toggle={toggleImportModal}>
            Importar Gastos Particulares
          </ModalHeader>
          <ModalBody>
            <div className="d-flex justify-content-between mb-3">
              <p className="fs--1 font-weight-semi-bold mb-0">
                Descargue la plantilla para el tipo de importación:
              </p>
              <DownloadFormatDropdown
                label="Descargar Plantilla"
                onSelect={downloadTemplate}
              />
            </div>
            <FalconDropzone
              files={importFile}
              onChange={(files) => {
                setImportFile(files);
              }}
              multiple={false}
              preview
              accept=".xlsx, .xls, .csv, .txt"
              placeholder={
                <>
                  <div className="fs-2 mb-2">
                    <FontAwesomeIcon icon={faFileExcel} />
                  </div>
                  <p className="fs--1 mb-0">Arrastre su archivo aquí</p>
                </>
              }
            />
            <div className="mt-3">
              <p className="fs--1 text-muted mb-0 font-weight-semi-bold">
                Columnas requeridas para la importación:
              </p>
              <p className="fs--1 text-muted mb-0">
                El archivo debe contener las columnas necesarias para gastos particulares (identificador de propiedad, nombre del gasto, monto, descripción, etc.).
              </p>
              {/* HO-TASK-249 */}
              <p className="fs--1 text-muted mb-0">
                Formatos aceptados: Excel (.xlsx, .xls) y texto (.csv, .txt).
              </p>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleImportModal}>
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleImport}
              disabled={importFile.length === 0 || loading}
            >
              {loading ? "Importando..." : "Importar"}
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={ownersModalOpen} toggle={() => setOwnersModalOpen(false)}>
          <ModalHeader toggle={() => setOwnersModalOpen(false)}>
            Propietarios asociados al gasto
          </ModalHeader>
          <ModalBody>
            {ownersModalList.length > 0 ? (
              <ul className="pl-3 mb-0">
                {ownersModalList.map((ownerName, ownerIndex) => (
                  <li key={`${ownerName}-${ownerIndex}`} className="mb-1">
                    {ownerName}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mb-0 text-muted">No hay propietarios para mostrar.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setOwnersModalOpen(false)}>
              Cerrar
            </Button>
          </ModalFooter>
        </Modal>
      </Fragment>
    </Fragment>
  );
};

IndividualCosts.propTypes = {
  showPagination: PropTypes.bool,
};

export default withRedirect(IndividualCosts);
